import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/admin/AuthContext";
import { BrandMark } from "@/components/politics/brand";
import { LogOut, LayoutDashboard, UserSquare2, Building2, Inbox, ShieldCheck } from "lucide-react";

const ROLE_LABEL = {
  admin: { text: "Administrador RSM", color: "#208BFF" },
  partner: { text: "Parceiro", color: "#7E20FF" },
  visitor: { text: "Sem acesso", color: "#D94A0A" },
};

export default function AdminLayout({ children, title, subtitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = ROLE_LABEL[user?.role] || ROLE_LABEL.visitor;

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F5FAFB]">
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className="lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-[#1B1D1D]/8 bg-white"
      >
        <div className="px-5 py-5 border-b border-[#1B1D1D]/6 flex items-center gap-2.5">
          <BrandMark size={28} color="#1B1D1D" />
          <span className="font-display text-base font-semibold tracking-tight text-[#1B1D1D]">
            RSM <span className="text-[#7D8188]">Admin</span>
          </span>
        </div>

        <nav className="px-3 py-4 flex flex-col gap-1">
          <NavItem to="/admin" icon={LayoutDashboard} label="Início" exact />
          {user?.role === "partner" && (
            <NavItem
              to="/admin/perfil"
              icon={UserSquare2}
              label="Meu perfil"
              testid="nav-perfil"
            />
          )}
          {user?.role === "admin" && (
            <>
              <NavItem
                to="/admin/parceiros"
                icon={Building2}
                label="Parceiros"
                testid="nav-parceiros"
              />
              <NavItem
                to="/admin/leads"
                icon={Inbox}
                label="Leads"
                testid="nav-leads"
              />
              <NavItem
                to="/admin/privacidade"
                icon={ShieldCheck}
                label="Política de privacidade"
                testid="nav-privacidade"
              />
            </>
          )}
        </nav>

        <div className="mt-auto px-5 py-5 border-t border-[#1B1D1D]/6">
          {user && (
            <div data-testid="admin-user-card" className="space-y-2">
              <div className="flex items-center gap-3">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name || user.email}
                    className="h-9 w-9 rounded-full"
                  />
                ) : (
                  <div className="h-9 w-9 rounded-full bg-[#1B1D1D] grid place-items-center text-white text-xs font-bold">
                    {(user.name || user.email).slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#1B1D1D] truncate">
                    {user.name || user.email}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: role.color }}>
                    {role.text}
                  </div>
                </div>
              </div>
              <button
                onClick={doLogout}
                data-testid="admin-logout"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#1B1D1D]/12 px-3 py-2 text-xs font-medium text-[#51565F] hover:border-[#D94A0A] hover:text-[#D94A0A] transition-colors"
              >
                <LogOut size={13} /> Sair
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
        <div className="max-w-5xl">
          {title && (
            <header className="mb-8">
              <h1
                data-testid="admin-page-title"
                className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-[#1B1D1D]"
              >
                {title}
              </h1>
              {subtitle && (
                <p className="mt-2 text-sm text-[#51565F] max-w-2xl">{subtitle}</p>
              )}
            </header>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, exact = false, testid }) {
  return (
    <NavLink
      to={to}
      end={exact}
      data-testid={testid || `nav-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          isActive
            ? "bg-[#1B1D1D] text-white"
            : "text-[#51565F] hover:bg-[#F5FAFB]"
        }`
      }
    >
      <Icon size={16} />
      {label}
    </NavLink>
  );
}
